import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { IResponseHandlerOptions, useResponseHandler } from 'utils/service/responseHandler';
import {
  ICreateCustomSectionTemplate,
  ICustomSectionTemplateResponse,
  IUpdateCustomSectionTemplate,
} from './types';
import { ITemplatesLightListItem } from '../types';

export const useGetSectionTemplateLightList = (enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITemplatesLightListItem[], DefaultError>({
    queryKey: ['section-template-light-list'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `CustomSectionTemplate/LightList`,
      });

      return responseHandler(response, {
        groupName: `useGetSectionTemplateLightList`,
        notFoundPath: '/templates',
      });
    },
    enabled: enabled,
  });
};

export const useGetSectionTemplate = (
  id: string,
  options?: IResponseHandlerOptions<ICustomSectionTemplateResponse>,
  enabled?: boolean,
  retry?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ICustomSectionTemplateResponse, DefaultError>({
    queryKey: ['section-template', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `CustomSectionTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetSectionTemplate - ${id}`,
        notFoundPath: '/templates',
        ...options,
      });
    },
    retry,
    enabled,
  });
};

export const usePostSectionTemplate = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostSectionTemplate');
  return useMutation<string, DefaultError, ICreateCustomSectionTemplate>({
    async mutationFn(body) {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.customSectionTemplate.title,
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'CustomSectionTemplate',
        body ,
      });

      return responseHandler(response, {
        groupName: 'usePostSectionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutSectionTemplate = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutSectionTemplate');
  return useMutation<any, DefaultError, IUpdateCustomSectionTemplate>({
    async mutationFn(body) {
      loading(
        i18next.t('service-message.update.loading', {
          what: body.customSectionTemplate.title,
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'CustomSectionTemplate',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePutSectionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.title,
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteSectionTemplate = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteSectionTemplate'
  );
  return useMutation<boolean, DefaultError>({
    async mutationFn() {
      loading(
        i18next.t('service-message.update.loading', {
          what: '',
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `CustomSectionTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteSectionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};
